import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { Visibility, Close, Download } from '@mui/icons-material';

// Utility function to format date to yyyy-MM-dd
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    return dateString; // Return original if parsing fails
  }
};

interface Field {
  type: string;
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[];
  multiple?: boolean;
}

interface FormProps<T> {
  fields: Field[];
  initialValues: T;
  onSubmit: (data: T) => void;
  onCancel: () => void;
  isSubmitted?: boolean;
  buttons?: {
    id: string;
    children: string;
    type: 'submit' | 'button';
    variant?: 'contained' | 'outlined';
    fullWidth?: boolean;
    sx?: React.CSSProperties;
  }[];
}

const Form = <T extends Record<string, any>>({
  fields,
  initialValues,
  onSubmit,
  onCancel,
  buttons,
}: FormProps<T>) => {
  const [formData, setFormData] = useState<T>(() => {
    const formattedValues = { ...initialValues } as T;
    fields.forEach(field => {
      if (field.type === 'date' && formattedValues[field.name as keyof T]) {
        formattedValues[field.name as keyof T] = formatDate(String(formattedValues[field.name as keyof T])) as any;
      }
    });
    return formattedValues;
  });

  const [imagePreviews, setImagePreviews] = useState<Record<string, string>>({});
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    const checked = (target as HTMLInputElement).checked;

    if (target.type === 'file') {
      const file = (target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('File size must be less than 5MB');
          target.value = '';
          return;
        }

        // Create preview URL for the new image
        const previewUrl = URL.createObjectURL(file);
        setImagePreviews(prev => ({
          ...prev,
          [name]: previewUrl
        }));

        setFormData({
          ...formData,
          [name]: file
        });
      }
    } else if (target.type === 'date') {
      setFormData({
        ...formData,
        [name]: value, // Store the date in yyyy-MM-dd format directly
      });
    } else if (target instanceof HTMLSelectElement && target.multiple) {
      const selectedOptions = Array.from(target.selectedOptions).map(option => option.value);
      setFormData({
        ...formData,
        [name]: selectedOptions.join(', '),
      });
      setActiveDropdown(null);
    } else {
      setFormData({
        ...formData,
        [name]: target.type === 'checkbox' ? checked : value,
      });
    }
  };

  const handleFocus = (fieldName: string) => {
    setActiveDropdown(fieldName);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Get only changed fields with values
    const changedFields = Object.keys(formData).reduce((acc, key) => {
      const value = formData[key];
      const initialValue = initialValues[key];

      // Skip if value is empty/null/undefined
      if (!value) return acc;

      // For files, check if it's a new upload
      if (value instanceof File || (value && value.buffer)) {
        if (value !== initialValue) {
          acc[key] = value;
        }
      } 
      // For other fields, check if value changed and is not empty
      else if (JSON.stringify(value) !== JSON.stringify(initialValue) && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    onSubmit(changedFields as T);
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleClearImage = (fieldName: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: null
    }));
    setImagePreviews(prev => ({
      ...prev,
      [fieldName]: ''
    }));
  };

  const handlePreviewImage = (url: string, name: string) => {
    setPreviewImage({ url, name });
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(imagePreviews).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviews]);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        {fields.map((field) => (
          <div
            key={field.name}
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            {field.label && <label htmlFor={field.name}>{field.label}</label>}
            {field.type === 'image' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(formData[field.name] || imagePreviews[field.name]) && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#f5f5f5'
                  }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {field.label}
                    </span>
                    <IconButton
                      size="small"
                      onClick={() => handlePreviewImage(
                        imagePreviews[field.name] || formData[field.name],
                        field.label || 'Image'
                      )}
                      style={{ color: '#666' }}
                    >
                      <Visibility />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const fileUrl = imagePreviews[field.name] || 
                          (typeof formData[field.name] === 'string' ? formData[field.name] : '');
                        if (fileUrl) {
                          handleDownload(fileUrl, field.label || 'file');
                        }
                      }}
                      style={{ color: '#666' }}
                    >
                      <Download />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleClearImage(field.name)}
                      style={{ color: '#666' }}
                    >
                      <Close />
                    </IconButton>
                  </div>
                )}
                <input
                  id={field.name}
                  type="file"
                  name={field.name}
                  onChange={handleChange}
                  accept=".png,.jpg,.jpeg,.pdf"
                  style={{
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                />
              </div>
            ) : field.type === 'dropdown' && field.multiple ? (
              <div 
                ref={dropdownRef}
                style={{ position: 'relative' }}
              >
                {activeDropdown === field.name ? (
                  <div
                    style={{
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      padding: '4px 0',
                      position: 'absolute',
                      width: '100%',
                      zIndex: 1000
                    }}
                  >
                    {field.options?.map((option) => {
                      const isSelected = (formData?.[field.name] as string)?.split(', ').includes(option.value);
                      return (
                        <div
                          key={option.value}
                          style={{
                            padding: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: isSelected ? '#f0f0f0' : 'transparent'
                          }}
                          onClick={() => {
                            const currentValues = (formData?.[field.name] as string)?.split(', ') || [];
                            const newValues = isSelected
                              ? currentValues.filter(v => v !== option.value)
                              : [...currentValues, option.value];
                            setFormData({
                              ...formData,
                              [field.name]: newValues.join(', ')
                            });
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            style={{ margin: 0 }}
                          />
                          <span>{option.label}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={formData?.[field.name] || ''}
                    onFocus={() => handleFocus(field.name)}
                    readOnly
                    placeholder={`Select ${field.label}`}
                    style={{
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  />
                )}
              </div>
            ) : field.type === 'dropdown' ? (
              <select
                id={field.name}
                name={field.name}
                value={(formData?.[field.name] as string) || ''}
                onChange={handleChange}
                required={field.required}
                style={{
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              >
                <option value="">Select {field.label}</option>
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'date' ? (
              <input
                id={field.name}
                type="date"
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleChange}
                required={field.required}
                style={{
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
            ) : (
              <input
                id={field.name}
                type={field.type === 'image' ? 'file' : field.type}
                name={field.name}
                placeholder={field.placeholder}
                value={field.type === 'image' ? undefined : (formData[field.name] || '')}
                onChange={handleChange}
                required={field.required}
                accept={field.type === 'image' ? '.png,.jpg,.jpeg,.pdf' : undefined}
                pattern={field.type === 'tel' ? '[0-9]{10}' : undefined}
                maxLength={field.type === 'tel' ? 10 : undefined}
                title={field.type === 'tel' ? 'Please enter a valid 10-digit phone number' : undefined}
                style={{
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                }}
              />
            )}
          </div>
        ))}
        <div
          style={{
            position: 'sticky',
            bottom: '0px',
            backgroundColor: '#fff',
            padding: '16px 0',
            display: 'flex',
            gap: '8px',
            marginTop: '16px',
            zIndex: 1000, // Ensures the buttons are above other elements
          }}
        >
          {buttons?.map((button) => (
            <button
              key={button.id}
              type={button.type}
              onClick={
                button.type === 'button' && button.id === 'cancel-button'
                  ? (e) => {
                      e.preventDefault();
                      handleCancel();
                    }
                  : undefined
              }
              style={{
                width: button.fullWidth ? '100%' : 'auto',
                padding: '10px',
                backgroundColor:
                  button.variant === 'contained' ? '#4a5568' : '#fff',
                color: button.variant === 'contained' ? '#fff' : '#007BFF',
                border:
                  button.variant === 'outlined' ? '1px solid #007BFF' : 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                ...button.sx,
              }}
            >
              {button.children}
            </button>
          ))}
        </div>
      </form>

      <Dialog
        open={!!previewImage}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogContent style={{ padding: '16px', position: 'relative' }}>
          <IconButton
            onClick={handleClosePreview}
            style={{
              position: 'absolute',
              right: '8px',
              top: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)'
            }}
          >
            <Close />
          </IconButton>
          {previewImage && (
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ marginBottom: '16px' }}>{previewImage.name}</h3>
              <img
                src={previewImage.url}
                alt={previewImage.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Form;
